import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useTenant } from '../providers/TenantProvider';
import { useOrganization } from '../features/organization';

export function Organization() {
  const { t } = useTranslation();
  const { currentTenant } = useTenant();
  const { organization, isLoading, error, fetchOrganization, updateOrganization } =
    useOrganization();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Branding state
  const [primaryColor, setPrimaryColor] = useState('#000000');

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    if (organization) {
      setName(organization.name);
    } else if (currentTenant) {
      setName(currentTenant.name);
    }
  }, [organization, currentTenant]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await updateOrganization({ name });
      setSaveMessage({ type: 'success', text: 'Organization profile saved successfully' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Failed to save organization profile' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Branding save would go here when API supports it
      setSaveMessage({ type: 'success', text: 'Branding saved successfully' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Failed to save branding' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !organization) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('organization.title')}</h1>
        <p className="text-muted-foreground">{t('organization.subtitle')}</p>
      </div>

      {saveMessage && (
        <div
          className={`p-4 rounded-lg ${
            saveMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">{error}</div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('organization.profile')}</CardTitle>
          <CardDescription>Update your organization details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="org-name" className="text-sm font-medium">
              {t('organization.name')}
            </label>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Organization name"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="org-slug" className="text-sm font-medium">
              {t('organization.slug')}
            </label>
            <Input id="org-slug" value={currentTenant?.slug || ''} disabled />
            <p className="text-xs text-muted-foreground">
              The slug cannot be changed after creation
            </p>
          </div>
          <div className="space-y-2">
            <label htmlFor="org-description" className="text-sm font-medium">
              {t('organization.description')}
            </label>
            <Input
              id="org-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a description for your organization"
            />
          </div>
          <Button onClick={handleSaveProfile} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('organization.saveChanges')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('organization.branding')}</CardTitle>
          <CardDescription>Customize your organization appearance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('organization.logo')}</label>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                <span className="text-2xl font-bold text-muted-foreground">
                  {name?.[0]?.toUpperCase() || 'O'}
                </span>
              </div>
              <div className="space-y-2">
                <Button variant="outline">
                  <Upload className="mr-2 h-4 w-4" />
                  {t('organization.uploadLogo')}
                </Button>
                <p className="text-xs text-muted-foreground">Recommended: 256x256px, PNG or JPG</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="primary-color" className="text-sm font-medium">
              {t('organization.primaryColor')}
            </label>
            <div className="flex items-center gap-2">
              <div
                className="h-10 w-10 rounded-lg border"
                style={{ backgroundColor: primaryColor }}
              />
              <Input
                id="primary-color"
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-20 h-10 p-1 cursor-pointer"
              />
              <Input
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-28"
                placeholder="#000000"
              />
            </div>
          </div>
          <Button onClick={handleSaveBranding} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('organization.saveChanges')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
